// Service page content for Skilled Independent (Subclass 189).
// Content sourced from docs/research/australian-visa-pathways.md §1.
// COMPLIANCE: we are not a registered migration agent and do not give migration
// advice or assess eligibility; the regulated advice and lodgement are a
// registered migration agent's. No guaranteed outcomes; honest about
// competitiveness; no unverified dollar figures stated as fact (figures flagged
// "confirm" per CLAUDE.md figures discipline). "Who does what" disclosure and
// disclaimer are rendered by the shared page template.

const skilledIndependent189 = {
  groupSlug: "skilled-migration",
  slug: "skilled-independent-189",
  title: "Skilled Independent visa (Subclass 189)",
  eyebrow: "Skilled Migration",
  intro:
    "The Subclass 189 is a points-tested permanent visa for skilled workers who are not sponsored by an employer, a state, or a family member. You are invited to apply through SkillSelect, and if granted, you receive permanent residence straight away.",

  // Quick-read summary chips (layout renders these at the top).
  chips: [
    { label: "Visa type", value: "Permanent residence" },
    { label: "Sponsor", value: "Not required" },
    { label: "Points-tested", value: "Yes, by invitation" },
    { label: "Apply via", value: "SkillSelect (EOI)" },
  ],

  whoFor: [
    "Skilled workers with an occupation on the applicable list who want permanent residence without needing a sponsor.",
    "Applicants confident their points score is competitive, not just above the minimum.",
    "People who want the freedom to live and work anywhere in Australia.",
  ],

  // Eligibility essentials checklist (honest, not a promise of approval).
  essentials: [
    {
      title: "An invitation through SkillSelect",
      detail:
        "You cannot apply directly. You submit an Expression of Interest (EOI) and wait to be invited in a selection round.",
    },
    {
      title: "A positive skills assessment",
      detail:
        "Your nominated occupation must be on the applicable list (the 189 generally draws on the MLTSSL) and assessed by the relevant assessing authority.",
    },
    {
      title: "Under 45 at the time of invitation",
      detail: "Age also affects your points score.",
    },
    {
      title: "At least competent English",
      detail:
        "Competent English is the minimum to qualify; higher levels add points and make you more competitive.",
    },
    {
      title: "Meet, and beat, the points pass mark",
      detail:
        "The pass mark lets you into the pool. The score that actually gets invited is usually well above it (see the honesty note below).",
    },
    {
      title: "Health and character requirements",
      detail:
        "All applicants and included family members must meet the Department's health and character requirements.",
    },
  ],

  // Process steps (layout renders as a numbered journey).
  process: [
    {
      title: "Skills assessment",
      detail:
        "A registered migration agent confirms your occupation, and we organise and prepare the evidence to obtain a positive assessment from the right assessing authority.",
    },
    {
      title: "English testing",
      detail:
        "A registered migration agent advises on the test and score you need to qualify and to maximise your points, and we help you get your results in order.",
    },
    {
      title: "Prepare your EOI",
      detail:
        "We build your Expression of Interest file to a lodgement standard, with the evidence organised and gaps closed. A registered migration agent finalises the points claim and lodges it in SkillSelect.",
    },
    {
      title: "Receive an invitation",
      detail:
        "If you are invited in a selection round, you have a limited window to apply. We get your file ready in advance and keep it moving.",
    },
    {
      title: "Lodge the visa application",
      detail:
        "We prepare a thorough, well-documented file. A registered migration agent lodges the application and deals with the Department.",
    },
    {
      title: "Decision",
      detail:
        "The Department decides. If granted, the 189 is permanent residence from the start.",
    },
  ],

  faqs: [
    {
      q: "How many points do I need for the 189?",
      a: "The minimum to be eligible is 65 points, but that only puts you in the pool. The score that actually receives an invitation is usually higher and varies by occupation. A registered migration agent assesses your realistic points and tells you honestly whether the 189 is competitive for you, or whether a nominated or regional option is a better bet. We organise the evidence behind whichever path you take.",
    },
    {
      q: "Do I need a sponsor or employer?",
      a: "No. The 189 is independent, so you do not need an employer, a state nomination, or a family sponsor. That is its main advantage. The trade-off is that it is competitive and relies entirely on your points.",
    },
    {
      q: "What if my occupation is not on the list?",
      a: "If your occupation is not on the applicable list for the 189, that specific pathway is not available, no matter how high your points are. A registered migration agent checks this first and, if needed, looks at state-nominated (190), regional (491), or employer-sponsored options where your occupation may be eligible. We prepare the file for whichever pathway applies.",
    },
    {
      q: "How long does it take?",
      a: "Processing times are set by the Department of Home Affairs and vary by case. They are commonly measured in months, on top of the time to get a skills assessment, sit English tests, and receive an invitation. We give you a realistic timeline for your situation rather than a best-case promise.",
    },
    {
      q: "What is the difference between the 189 and the 190?",
      a: "The 189 needs no nomination but is more competitive on points. The 190 requires a state or territory nomination, which adds 5 points and can make invitation more achievable, in exchange for a commitment to that state. A registered migration agent helps you weigh which suits you.",
    },
  ],

  related: [
    "skilled-nominated-190",
    "skilled-regional-491",
    "skills-assessment",
  ],

  lastVerified: "13 July 2026",
};

export default skilledIndependent189;
